import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useUIVersion } from '@/utils/UIVersionContext'

type Theme = 'dark' | 'light' | 'system'
type ResolvedTheme = Exclude<Theme, 'system'>

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  toggleTheme: () => void
  resolvedTheme: ResolvedTheme
  setTheme: (theme: Theme) => void
}

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined)

const isTheme = (value: string | null): value is Theme =>
  value === 'light' || value === 'dark' || value === 'system'

const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'tickmill-ui-theme',
  ...props
}: ThemeProviderProps) {
  const { isNewUI } = useUIVersion()
  const [theme, setThemeState] = useState<Theme>(() => {
    const storedTheme = localStorage.getItem(storageKey)
    const nextTheme = isTheme(storedTheme) ? storedTheme : defaultTheme

    if (!isNewUI && nextTheme === 'dark') {
      return 'light'
    }

    return nextTheme
  })
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light')
    }

    setSystemTheme(mediaQuery.matches ? 'dark' : 'light')
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange)
    } else {
      mediaQuery.addListener(handleChange)
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleChange)
      } else {
        mediaQuery.removeListener(handleChange)
      }
    }
  }, [])

  const requestedTheme = theme === 'system' ? systemTheme : theme
  const resolvedTheme: ResolvedTheme = isNewUI ? requestedTheme : 'light'

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')
    root.classList.add(resolvedTheme)
    localStorage.setItem(storageKey, theme)
  }, [resolvedTheme, storageKey, theme])

  const setTheme = useCallback(
    (nextTheme: Theme) => {
      if (!isNewUI && nextTheme === 'dark') {
        setThemeState('light')
        return
      }

      setThemeState(nextTheme)
    },
    [isNewUI]
  )

  const toggleTheme = useCallback((): void => {
    setThemeState((currentTheme) => {
      if (!isNewUI) {
        return currentTheme === 'light' ? 'system' : 'light'
      }

      if (currentTheme === 'system') {
        return resolvedTheme === 'dark' ? 'light' : 'dark'
      }

      return currentTheme === 'light' ? 'dark' : 'light'
    })
  }, [isNewUI, resolvedTheme])

  useEffect(() => {
    if (!isNewUI && theme === 'dark') {
      setThemeState('light')
    }
  }, [isNewUI, theme])

  const selectableTheme: Theme = !isNewUI && theme === 'dark' ? 'light' : theme

  const value = useMemo(
    () => ({
      theme: selectableTheme,
      resolvedTheme,
      toggleTheme,
      setTheme,
    }),
    [resolvedTheme, selectableTheme, setTheme, toggleTheme]
  )

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
